// useVideoDuration
// ────────────────
// Returns the real, LIVE duration of a module / intro video regardless
// of whether it renders as a native <video>, a Vimeo iframe, or a
// Vimeo embed-code paste.
//
// Why this exists
// ---------------
// The module summary shows "Video time: 1h 45m" per module. Before
// this hook the value came from a hardcoded `MODULE_EXTRAS` map in
// ModuleDetail.jsx, so if an admin swapped the source Vimeo clip
// (which happens a lot as we re-record lessons) the shown duration
// stayed frozen at the launch-day value. For native <video> playback
// the existing `onLoadedMetadata` handler already captured the true
// duration off the DOM — but for Vimeo iframes and embed HTML the
// iframe is opaque and there's no `loadedmetadata` event to hook.
//
// Vimeo publishes a free, CORS-open oEmbed endpoint that returns the
// authoritative duration in seconds for any public or unlisted video:
//
//   GET https://vimeo.com/api/oembed.json?url=<canonical vimeo url>
//   → { duration: 6234, thumbnail_url, ... }
//
// This hook parses ANY of the shapes an admin might have saved
// (`https://vimeo.com/12345`, `https://vimeo.com/12345/abcd1234`,
// `https://player.vimeo.com/video/12345`, or a full `<div ...><iframe
// src="https://player.vimeo.com/video/12345...">…</iframe>...</div>`
// embed paste), canonicalizes it to `https://vimeo.com/<id>`, and
// queries oEmbed. Results are cached module-level so opening a page
// twice doesn't refetch, and swapping to a new URL clears the stale
// cell.
//
// YouTube: their oEmbed does NOT expose duration and the Data API
// requires a key — so YouTube keeps its existing behavior (falls back
// to the hardcoded label). If we ever wire a server-side proxy this
// hook is the single seam to bolt it onto.
//
// Failure modes are all graceful: the hook returns `null` and the
// caller falls back to the hardcoded map, so a Vimeo API outage never
// breaks the page.

import { useEffect, useRef, useState } from "react";

// url → duration in seconds (or null while pending)
const _cache = new Map();
// url → in-flight Promise so parallel hook mounts hit the network once
const _pending = new Map();

/**
 * Extract the numeric Vimeo video id from any of the input shapes we
 * accept, or return null when the URL isn't a Vimeo asset. This keeps
 * the hook resilient to raw embed HTML that also carries `<script>`
 * tags, tracking parameters, or unlisted-hash suffixes.
 */
function extractVimeoId(raw) {
  if (!raw || typeof raw !== "string") return null;
  // If admin pasted the full <div>…<iframe src="player.vimeo.com/video/12345?...">
  // snippet, pull the id out of the iframe src.
  const iframeMatch = raw.match(/player\.vimeo\.com\/video\/(\d+)/);
  if (iframeMatch) return iframeMatch[1];
  // Plain https://vimeo.com/12345, https://vimeo.com/12345/hash, etc.
  const plainMatch = raw.match(/vimeo\.com\/(?:video\/|manage\/videos\/)?(\d+)/);
  if (plainMatch) return plainMatch[1];
  return null;
}

/**
 * Fetch and cache the duration for a specific Vimeo id. Returns a
 * Promise<number | null>. Never throws — a fetch failure resolves to
 * `null` and the caller falls through to its own default.
 */
async function fetchVimeoDuration(vimeoId) {
  const cacheKey = `vimeo:${vimeoId}`;
  if (_cache.has(cacheKey)) return _cache.get(cacheKey);
  if (_pending.has(cacheKey)) return _pending.get(cacheKey);
  const canonical = `https://vimeo.com/${vimeoId}`;
  const oembed = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(canonical)}`;
  const p = (async () => {
    try {
      const resp = await fetch(oembed, {
        method: "GET",
        // No credentials — Vimeo's oEmbed is fully public and adding
        // credentials would break CORS.
        credentials: "omit",
      });
      if (!resp.ok) return null;
      const json = await resp.json();
      const secs = Number(json && json.duration);
      const value = Number.isFinite(secs) && secs > 0 ? secs : null;
      _cache.set(cacheKey, value);
      return value;
    } catch (_err) {
      _cache.set(cacheKey, null);
      return null;
    } finally {
      _pending.delete(cacheKey);
    }
  })();
  _pending.set(cacheKey, p);
  return p;
}

/**
 * Public hook. Returns `{ duration, loading, source }` where
 * `duration` is a number of seconds or `null` if unknown. `source`
 * is a small string ('vimeo' | null) that lets the caller show
 * different fallback labels during load vs when nothing is available.
 */
export function useVideoDuration(videoUrl) {
  const [state, setState] = useState({ duration: null, loading: false, source: null });
  // Track the URL the last effect ran against so a rapid replace →
  // replace in the admin flow doesn't set stale duration.
  const activeUrlRef = useRef(null);

  useEffect(() => {
    activeUrlRef.current = videoUrl || null;
    if (!videoUrl) {
      setState({ duration: null, loading: false, source: null });
      return;
    }
    const vimeoId = extractVimeoId(videoUrl);
    if (!vimeoId) {
      // Non-Vimeo URL → this hook doesn't know how to fetch. Caller
      // falls back to <video>.onLoadedMetadata for MP4 or to the
      // hardcoded label for YouTube.
      setState({ duration: null, loading: false, source: null });
      return;
    }
    // Cache hit — synchronous update, no loading flag flicker.
    const cacheKey = `vimeo:${vimeoId}`;
    if (_cache.has(cacheKey)) {
      setState({ duration: _cache.get(cacheKey), loading: false, source: "vimeo" });
      return;
    }
    setState((s) => ({ ...s, loading: true, source: "vimeo" }));
    let cancelled = false;
    fetchVimeoDuration(vimeoId).then((secs) => {
      if (cancelled) return;
      // Guard against a race where two rapid URL swaps land out of
      // order — only commit the response for the URL that's currently
      // active on the component.
      if (activeUrlRef.current !== videoUrl) return;
      setState({ duration: secs, loading: false, source: "vimeo" });
    });
    return () => { cancelled = true; };
  }, [videoUrl]);

  return state;
}

export default useVideoDuration;
